import { CheckoutPurchasePayload, Support } from "./support"

export function getCsrfToken(fallback?: string): string {
  const meta = document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null
  return meta?.content || fallback || ""
}

export async function getSupport(): Promise<Support> {
  const res = await fetch("/sign_up/support")
  if (!res.ok) throw new Error(`${res.status}: ${res.statusText}`)

  const json: Support = await res.json()
  return json
}

// "pending" means the visitor came back from Checkout before the payment
// settled (delayed payment methods); "error" covers everything transient.
export type CheckoutSessionEcommerceResult =
  | { state: "paid"; payload: CheckoutPurchasePayload }
  | { state: "pending" }
  | { state: "error" }

export async function getCheckoutSessionEcommerce(
  sessionId: string,
): Promise<CheckoutSessionEcommerceResult> {
  let res: Response
  try {
    const query = `session_id=${encodeURIComponent(sessionId)}`
    res = await fetch(`/sign_up/stripe_payment/checkout_session_ecommerce?${query}`, {
      headers: { Accept: "application/json" },
      credentials: "include",
    })
  } catch (error) {
    return { state: "error" }
  }
  if (!res.ok) return { state: "error" }

  try {
    const json = await res.json()
    if (json && json.pending) return { state: "pending" }
    if (json && json.ecommerce) return { state: "paid", payload: json as CheckoutPurchasePayload }
    return { state: "error" }
  } catch (error) {
    return { state: "error" }
  }
}

export async function send<T extends Object>({
  url,
  body,
  token,
  method = "post",
}: {
  url: string
  body: string
  token: string
  method?: string
}): Promise<{
  error?: string
  errors?: { [key in keyof T]?: string }
  success?: boolean
  retryable?: boolean
}> {
  let res: Response
  try {
    const csrfToken = getCsrfToken(token)
    res = await fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": csrfToken,
      },
      credentials: "include",
      body,
    })
  } catch (error) {
    let message = "Something went wrong. Please, try again or reload the page."
    if (error instanceof Error) message += ` (${error.name}: ${error.message})`

    return { error: message }
  }

  try {
    const json = await res.json()
    if (Object.hasOwnProperty.call(json, "success")) return { success: json.success }
    // The sign-up bot check failed: a form-level message, and one retry with a fresh token
    if (json.retryable) {
      const message = json.errors?.[0]?.message || "Something went wrong. Please try again."
      return { error: message, retryable: true }
    }
    return { errors: json }
  } catch (error) {
    let message = "Something went wrong. Please, try again or reload the page."
    if (!res.ok) message += ` (${res.status}: ${res.statusText})`
    else if (error instanceof SyntaxError) message += " (Bad response)"
    else if (error instanceof Error) message += ` (${error.name}: ${error.message})`

    return { error: message }
  }
}
